<template>
  <div class="imd-page" v-loading="loading">
    <div class="page">
      <div class="page-header">
        <div>
          <div class="page-title">商品详情会员优惠配置</div>
          <div class="page-desc">维护适用商品、积分门槛与非会员引导注册开关</div>
        </div>
      </div>

      <div class="layout">
        <div class="config-box">
          <div class="box-title">基础配置</div>
          <div class="box-body">
            <div class="cfg-grid">
              <div class="cfg-label">非会员引导注册</div>
              <div class="cfg-field">
                <div class="cfg-switch-line">
                  <el-switch
                    v-model="form.nonMemberDisplay"
                    active-value="show"
                    inactive-value="hide"
                    active-color="#2a78ff"
                    inactive-color="#cdd5e4"
                  />
                  <span class="cfg-lead">开启后，非会员访问适用商品详情时展示入会引导（与「会员礼遇」券/积分并行互斥，非会员不出现礼遇行）</span>
                </div>
                <p class="cfg-note">关闭则非会员在详情页不出现该引导。</p>
              </div>

              <div class="cfg-label required">显示积分的最低积分数量</div>
              <div class="cfg-field">
                <el-input-number
                  v-model="form.minPointsDisplay"
                  class="points-input"
                  :min="0"
                  :precision="0"
                  :controls="false"
                  placeholder="请输入整数"
                />
                <p class="cfg-desc">请填写非负整数。仅会员、无券时与积分比较。</p>
              </div>
            </div>
          </div>
        </div>

        <div class="product-box">
          <div class="box-title">商品</div>
          <div class="product-inner">
            <div class="select-row">
              <div class="select-label">适用商品</div>
              <div class="select-like">
                <el-select
                  v-model="form.goodsScope"
                  class="select-merge"
                  popper-class="detail-member-discount-select"
                >
                  <el-option label="指定商品适用" value="specific" />
                  <el-option label="全部商品适用" value="all" />
                </el-select>
              </div>
            </div>

            <template v-if="form.goodsScope === 'specific'">
              <div class="action-row action-row--below-scope">
                <button type="button" class="primary-btn" @click="handlePickGoods">选择商品</button>
                <div class="upload-inline">
                  <el-upload
                    action=""
                    :auto-upload="false"
                    :show-file-list="false"
                    :on-change="uploadHandleChange"
                  >
                    <button type="button" class="primary-btn">批量上传</button>
                  </el-upload>
                </div>
                <button type="button" class="primary-btn" @click="downloadTemplate">下载模板</button>
              </div>

              <div class="hint hint--after-actions">
                批量上传与营销中心订单兑礼导入字段一致；货号为 SKU 编码，导入后按商品（SPU）去重展示。
              </div>

              <div
                v-for="block in groupedGoodsBlocks"
                :key="block.key"
                class="selected-goods"
              >
                <div class="goods-thumb-wrap">
                  <img
                    v-if="thumbUrl(block)"
                    :src="thumbUrl(block)"
                    alt=""
                    class="goods-thumb-img"
                  >
                  <span v-else class="goods-thumb" />
                </div>
                <div class="goods-info">
                  <div class="goods-name-line">
                    <span class="goods-name">{{ block.title }}</span>
                    <el-button
                      type="text"
                      icon="el-icon-delete"
                      class="goods-delete-btn"
                      aria-label="删除商品"
                      @click="removeGoodsBlock(block)"
                    />
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="hint scope-tip">选择「全部商品适用」时无需指定商品。</div>
          </div>
        </div>

        <div class="footer-actions">
          <button type="button" class="ghost-btn" @click="handleCancel">取消</button>
          <button type="button" class="primary-btn" :disabled="submitting" @click="handleSubmit">
            {{ submitting ? '提交中…' : '提交' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { handleUploadFile, exportUploadTemplate } from '@/api/common'
import {
  getDetailMemberDiscountConfig,
  saveDetailMemberDiscountConfig
} from '@/api/member'

export default {
  name: 'DetailMemberDiscount',
  data() {
    return {
      loading: false,
      submitting: false,
      savingTop: false,
      form: {
        nonMemberDisplay: 'show',
        minPointsDisplay: 0,
        goodsScope: 'specific',
        items: []
      }
    }
  },
  computed: {
    groupedGoodsBlocks() {
      return (this.form.items || []).map((row, idx) => ({
        key: `g-${row.goods_id}-${idx}`,
        title: row.goods_name || row.item_name || row.goods_bn || row.item_bn || '商品',
        rows: [row]
      }))
    }
  },
  watch: {
    'form.goodsScope'(val) {
      if (val !== 'specific') {
        this.form.items = []
      }
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    thumbUrl(block) {
      const first = block.rows && block.rows[0]
      if (!first) return ''
      if (first.pics && first.pics[0]) return first.pics[0]
      if (first.pic) return first.pic
      return ''
    },
    unwrapPayload(res) {
      const body = res && res.data !== undefined ? res.data : res
      if (body && Object.prototype.hasOwnProperty.call(body, 'data')) {
        return body.data
      }
      return body
    },
    applyPayload(payload) {
      if (!payload || typeof payload !== 'object') return
      if (payload.non_member_display !== undefined) this.form.nonMemberDisplay = payload.non_member_display
      if (payload.min_points_display !== undefined && payload.min_points_display !== null) {
        this.form.minPointsDisplay = Number(payload.min_points_display)
      }
      if (payload.goods_scope !== undefined) this.form.goodsScope = payload.goods_scope
      if (Array.isArray(payload.items)) this.form.items = payload.items
    },
    buildSavePayload() {
      const minPts =
        this.form.minPointsDisplay === '' || this.form.minPointsDisplay === null || Number.isNaN(Number(this.form.minPointsDisplay))
          ? 0
          : parseInt(String(this.form.minPointsDisplay), 10)
      return {
        non_member_display: this.form.nonMemberDisplay,
        min_points_display: minPts,
        goods_scope: this.form.goodsScope,
        items: this.form.goodsScope === 'specific' ? this.form.items : []
      }
    },
    async loadConfig() {
      this.loading = true
      try {
        const res = await getDetailMemberDiscountConfig()
        this.applyPayload(this.unwrapPayload(res))
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    handleBack() {
      this.$router.back()
    },
    async persist(messageOk) {
      if (this.form.goodsScope === 'specific' && (!this.form.items || !this.form.items.length)) {
        this.$message.warning('请添加适用商品（指定商品适用时至少选一个商品）')
        return
      }
      try {
        const res = await saveDetailMemberDiscountConfig(this.buildSavePayload())
        this.applyPayload(this.unwrapPayload(res))
        this.$message.success(messageOk || '保存成功')
      } catch (e) {
        console.error(e)
      }
    },
    async handleSaveConfig() {
      if (this.form.goodsScope === 'specific' && (!this.form.items || !this.form.items.length)) {
        this.$message.warning('请添加适用商品（指定商品适用时至少选一个商品）')
        return
      }
      this.savingTop = true
      try {
        const res = await saveDetailMemberDiscountConfig(this.buildSavePayload())
        this.applyPayload(this.unwrapPayload(res))
        this.$message.success('配置已保存')
      } catch (e) {
        console.error(e)
      } finally {
        this.savingTop = false
      }
    },
    handleCancel() {
      this.$router.back()
    },
    async handleSubmit() {
      if (this.form.goodsScope === 'specific' && (!this.form.items || !this.form.items.length)) {
        this.$message.warning('请添加适用商品（指定商品适用时至少选一个商品）')
        return
      }
      this.submitting = true
      try {
        const res = await saveDetailMemberDiscountConfig(this.buildSavePayload())
        this.applyPayload(this.unwrapPayload(res))
        this.$message.success('提交成功')
      } catch (e) {
        console.error(e)
      } finally {
        this.submitting = false
      }
    },
    normalizeGoodsRow(row) {
      const r = row && typeof row === 'object' ? row : {}
      const gid = r.goods_id != null ? Number(r.goods_id) : 0
      return {
        goods_id: gid,
        goods_bn: r.goods_bn != null && r.goods_bn !== '' ? String(r.goods_bn) : String(r.item_bn || ''),
        goods_name: String(r.goods_name || r.itemName || r.page_name || r.item_name || ''),
        pics: Array.isArray(r.pics) ? r.pics : [],
        isValidSwitch: !!r.isValidSwitch,
        isValidSwitchWareHouse: !!r.isValidSwitchWareHouse
      }
    },
    /**
     * 增量合并：保留已选商品；本次选择中已存在的更新展示字段并保留开关；本次新选的追加到末尾。
     */
    mergePickerRows(data) {
      let payload = data
      if (payload && !Array.isArray(payload) && Array.isArray(payload.data)) {
        payload = payload.data
      }
      const raw = Array.isArray(payload) ? payload : []
      const incoming = raw.map((row) => this.normalizeGoodsRow(row)).filter((row) => row.goods_id > 0)

      const existing = (this.form.items || [])
        .map((row) => this.normalizeGoodsRow(row))
        .filter((row) => row.goods_id > 0)

      const mergedByGid = {}
      existing.forEach((r) => {
        mergedByGid[r.goods_id] = { ...r }
      })

      incoming.forEach((item) => {
        const gid = item.goods_id
        const prev = mergedByGid[gid]
        if (prev) {
          mergedByGid[gid] = {
            ...prev,
            ...item,
            isValidSwitch: prev.isValidSwitch,
            isValidSwitchWareHouse: prev.isValidSwitchWareHouse
          }
        } else {
          mergedByGid[gid] = {
            ...item,
            isValidSwitch: false,
            isValidSwitchWareHouse: false
          }
        }
      })

      const incomingOrder = []
      const seenIncomingGid = {}
      incoming.forEach((item) => {
        const gid = item.goods_id
        if (seenIncomingGid[gid]) return
        seenIncomingGid[gid] = true
        incomingOrder.push(gid)
      })

      const out = []
      const used = {}
      existing.forEach((r) => {
        const gid = r.goods_id
        if (used[gid] || !mergedByGid[gid]) return
        out.push(mergedByGid[gid])
        used[gid] = true
      })
      incomingOrder.forEach((gid) => {
        if (used[gid]) return
        out.push(mergedByGid[gid])
        used[gid] = true
      })

      this.form.items = out
    },
    async handlePickGoods() {
      const res = await this.$picker.goods({
        data: this.form.items,
        paramsFieldExclude: ['distributor_id'],
        queryParams: {}
      })
      const payload = res && res.data !== undefined ? res.data : res
      this.mergePickerRows(payload)
    },
    removeGoodsBlock(block) {
      const gid = block.rows[0] && block.rows[0].goods_id
      if (gid == null) return
      this.form.items = this.form.items.filter((r) => Number(r.goods_id) !== Number(gid))
    },
    uploadHandleChange(file) {
      const params = {
        isUploadFile: true,
        file_type: 'item_member_discount_goods',
        file: file.raw
      }
      handleUploadFile(params).then((response) => {
        this.$message.success('上传成功')
        const { data } = response.data
        if (data.fail && data.fail.length > 0) {
          const str = data.fail.map((item) => item.item_bn).join(',')
          setTimeout(() => {
            this.$message({
              showClose: true,
              message: `以下商品编号不存在：${str}`,
              type: 'error',
              duration: 100000
            })
          }, 1500)
        }
        if (!data.succ || data.succ.length <= 0) return
        this.mergePickerRows(data.succ)
      })
    },
    downloadTemplate() {
      const params = {
        file_type: 'item_member_discount_goods',
        file_name: '商品详情会员优惠商品模板'
      }
      exportUploadTemplate(params).then((response) => {
        const { data } = response.data
        if (data.file) {
          const a = document.createElement('a')
          a.href = data.file
          a.download = data.name
          document.body.appendChild(a)
          a.click()
          a.remove()
        } else {
          this.$message.error('没有相关数据可导出')
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
/* 与原型 后台-商品详情会员优惠配置 (1).html 主内容区样式对齐 */
.imd-page {
  box-sizing: border-box;
  min-height: 100%;
  margin: -20px -20px 0;
  padding: 0 0 24px;
  background: #f5f7fb;
  color: #2f3440;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei',
    sans-serif;
}

.imd-page *,
.imd-page *::before,
.imd-page *::after {
  box-sizing: border-box;
}

.topbar {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e9edf3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}

.breadcrumbs {
  font-size: 13px;
  color: #7b8393;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ghost-btn,
.primary-btn {
  height: 34px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.ghost-btn {
  border: 1px solid #d7ddea;
  background: #fff;
  color: #4d5666;
}

.primary-btn {
  border: 1px solid #2a78ff;
  background: #2a78ff;
  color: #fff;
}

.primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.page {
  padding: 24px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #242b38;
}

.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #7b8393;
}

.layout {
  display: block;
}

.config-box {
  background: #fff;
  border: 1px solid #eceff5;
  border-radius: 4px;
}

.product-box {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #eceff5;
  border-radius: 4px;
}

.box-title {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #f1f3f7;
  font-size: 15px;
  color: #333b48;
  font-weight: 600;
}

.box-body,
.product-inner {
  padding: 20px 28px 18px;
}

.cfg-grid {
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: 22px 20px;
  align-items: start;
}

.cfg-label {
  padding-top: 6px;
  font-size: 14px;
  color: #4d5666;
  text-align: right;
  line-height: 1.5;
}

.cfg-label.required::before {
  content: '*';
  color: #ff4d4f;
  margin-right: 4px;
}

.cfg-field {
  min-width: 0;
}

.cfg-switch-line {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.cfg-switch-line ::v-deep .el-switch {
  flex-shrink: 0;
  margin-top: 2px;
}

.cfg-lead {
  flex: 1;
  font-size: 14px;
  color: #4d5666;
  line-height: 1.6;
}

.cfg-note {
  margin: 10px 0 0;
  font-size: 12px;
  color: #8a92a3;
  line-height: 1.6;
}

.cfg-desc {
  margin: 8px 0 0;
  font-size: 12px;
  color: #8a92a3;
  line-height: 1.6;
}

.points-input {
  width: 200px;

  ::v-deep .el-input__inner {
    height: 36px;
    line-height: 36px;
    padding: 0 14px;
    border-radius: 6px;
    border: 1px solid #dfe4ee;
    background: #fafbfd;
    font-size: 14px;
    font-weight: 500;
    color: #333b48;
    text-align: left;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }

  ::v-deep .el-input__inner:hover {
    border-color: #c5cdd9;
    background: #fff;
  }

  ::v-deep .el-input__inner:focus {
    border-color: #2a78ff;
    background: #fff;
    box-shadow: 0 0 0 2px rgba(42, 120, 255, 0.12);
  }

  ::v-deep .el-input__inner::placeholder {
    color: #c0c6d4;
    font-weight: 400;
  }
}

.select-row {
  display: grid;
  grid-template-columns: 84px minmax(0, 500px);
  gap: 12px;
  align-items: center;
}

.select-label {
  font-size: 14px;
  color: #5a6270;
  text-align: right;
}

.select-like {
  height: 36px;
  border: 1px solid #dfe4ee;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 0 0 12px;
  font-size: 14px;
  color: #4d5666;
  background: #fff;
  max-width: 500px;
}

.select-merge {
  width: 100%;

  ::v-deep .el-input__inner {
    border: none;
    height: 34px;
    line-height: 34px;
    padding-left: 0;
    padding-right: 28px;
    font-size: 14px;
    color: #4d5666;
    background: transparent;
  }

  ::v-deep .el-input .el-select__caret {
    color: #c0c6d4;
  }

  ::v-deep .el-input__suffix {
    right: 10px;
  }
}

.selected-goods {
  margin-top: 18px;
  margin-left: 84px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.goods-thumb-wrap {
  position: relative;
  width: 50px;
  height: 50px;
  border: 1px solid #edf1f7;
  border-radius: 4px;
  background: #f5f7fb;
  flex: 0 0 auto;
}

.goods-thumb-badge {
  position: absolute;
  left: -8px;
  top: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 4px;
  background: #ff2b2b;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  z-index: 1;
}

.goods-thumb {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 4px;
  background: linear-gradient(180deg, #3a3d49 0%, #1e2028 100%);
}

.goods-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 4px;
}

.goods-info {
  padding-top: 2px;
  min-width: 0;
}

.goods-name-line {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #333b48;
}

.goods-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #333b48;
}

.goods-delete-btn {
  flex-shrink: 0;
  padding: 4px !important;
  margin-left: 4px !important;
  font-size: 16px !important;
  color: #c0c4cc !important;

  &:hover {
    color: #f56c6c !important;
  }
}

.goods-subline {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #8a92a3;
}

.text-link {
  border: none;
  background: none;
  color: #2a78ff;
  font-size: 13px;
  padding: 0;
  cursor: pointer;
}

.action-row {
  margin-left: 84px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.action-row--below-scope {
  margin-top: 16px;
}

.upload-inline {
  display: inline-flex;
  align-items: center;
  line-height: 1;

  ::v-deep .el-upload {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    line-height: 1;
  }

  ::v-deep .el-upload:focus {
    outline: none;
  }

  .primary-btn {
    vertical-align: middle;
  }
}

.hint {
  margin-top: 8px;
  margin-left: 84px;
  font-size: 12px;
  color: #8a92a3;
  line-height: 1.6;
}

.hint.scope-tip {
  margin-left: 0;
  margin-top: 16px;
}

.footer-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 20px 0 0;
}
</style>
